import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Users, ArrowLeft, Crown, MessageSquare, Send, Loader2, User, Clock, LogIn, LogOut } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { pl } from 'date-fns/locale';
import { toast } from 'sonner';

export default function GroupDetailPage() {
  const { id } = useParams();
  const { user, token, authHeaders } = useAuth();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [sending, setSending] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const fetchGroup = async () => {
    try {
      const res = await axios.get(`${API}/groups/${id}`);
      setGroup(res.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchGroup(); }, [id]);

  const isMember = group && user && group.members?.includes(user.id);
  const isOwner = group && user && group.owner_id === user.id;

  const handleJoin = async () => {
    if (!token) { toast.error('Zaloguj się'); return; }
    try {
      const res = await axios.post(`${API}/groups/${group.id}/join`, null, { headers: authHeaders() });
      toast.success(res.data.status === 'joined' ? 'Dołączyłeś do grupy!' : 'Opuściłeś grupę');
      fetchGroup();
    } catch {}
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!newPost.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/groups/${group.id}/posts`, { text: newPost }, { headers: authHeaders() });
      setNewPost('');
      toast.success('+3 Zorki za post!');
      fetchGroup();
    } catch (err) { toast.error(err.response?.data?.detail || 'Błąd'); }
    setSending(false);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;
  if (!group) return <div className="text-center py-20 text-muted-foreground">Grupa nie znaleziona</div>;

  const cover = group.cover_image_local ? `${BACKEND}${group.cover_image_local}` : group.cover_image_url;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-20 md:pb-6" data-testid="group-detail-page">
      <SEO title={group.name} description={group.description} />

      <Link to="/groups" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-white mb-4 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Grupy
      </Link>

      {/* Header */}
      <div className="bg-card border border-border/40 overflow-hidden mb-6">
        {cover && <div className="h-32 sm:h-44 bg-secondary overflow-hidden"><img src={cover} alt="" className="w-full h-full object-cover" /></div>}
        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h1 className="font-heading text-2xl font-extrabold text-white">{group.name}</h1>
              <p className="text-sm text-muted-foreground mt-1">{group.description}</p>
              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {group.members_count} członków</span>
                <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> {group.posts_count} postów</span>
                <span className="flex items-center gap-1"><Crown className="w-3.5 h-3.5 text-zorki" /> {group.owner_nick}</span>
              </div>
            </div>
            {token && !isOwner && (
              <Button onClick={handleJoin} className={`rounded-none font-heading uppercase tracking-wider text-xs ${isMember ? 'bg-secondary text-secondary-foreground' : 'bg-primary text-primary-foreground'}`} data-testid="join-group-btn">
                {isMember ? <><LogOut className="w-3.5 h-3.5 mr-1" /> Opuść</> : <><LogIn className="w-3.5 h-3.5 mr-1" /> Dołącz</>}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* New post */}
      {isMember && (
        <form onSubmit={handlePost} className="flex gap-2 mb-6" data-testid="group-post-form">
          <Input value={newPost} onChange={e => setNewPost(e.target.value)} placeholder="Napisz do grupy..." className="flex-1 bg-input border-transparent focus:border-primary rounded-none" data-testid="group-post-input" />
          <Button type="submit" disabled={sending} className="bg-primary text-primary-foreground rounded-none px-4" data-testid="group-post-submit">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </form>
      )}
      {!token && <p className="text-sm text-muted-foreground mb-6"><Link to="/auth" className="text-primary hover:underline">Zaloguj się</Link> i dołącz do grupy aby pisać</p>}

      {/* Posts */}
      <div className="space-y-3" data-testid="group-posts">
        {(group.posts || []).map(p => (
          <div key={p.id} className="bg-card border border-border/30 p-4" data-testid={`group-post-${p.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center">
                <User className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
              <span className="text-sm font-medium text-white">{p.user_nick}</span>
              <span className="text-xs text-muted-foreground">
                {p.created_at ? formatDistanceToNow(new Date(p.created_at), { addSuffix: true, locale: pl }) : ''}
              </span>
            </div>
            <p className="text-sm text-foreground/80">{p.text}</p>
          </div>
        ))}
        {(!group.posts || group.posts.length === 0) && (
          <p className="text-sm text-muted-foreground text-center py-8">Brak postów. {isMember ? 'Napisz pierwszy!' : 'Dołącz i napisz pierwszy!'}</p>
        )}
      </div>
    </div>
  );
}
